import { useMemo } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGetEdgeMenuQuery } from "../../store/api";
import { buildEdgeMenuAccess } from "../../utils/edgeRbacMenu";
import {
  getUserExactPermissions,
  hasExactRoutePermission,
} from "../../utils/permissions";

const ACCESS_DENIED_MESSAGE =
  "You do not have access to this feature. If you think this is a mistake, contact an admin.";

const FALLBACK_PATHS = [
  "/pos",
  "/devices/health",
  "/menu/categories",
  "/menu/items",
  "/settings/configure-store",
  "/reporting/orders",
  "/cash-management/tills",
];

const EdgeMenuAccessGate = ({ children }) => {
  const location = useLocation();
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = Boolean(token && user);
  const { data: edgeMenu, isLoading, isError } = useGetEdgeMenuQuery(undefined, {
    skip: !isAuthenticated,
  });
  const edgeMenuAccess = useMemo(() => buildEdgeMenuAccess(edgeMenu), [edgeMenu]);
  const userPermissions = getUserExactPermissions(user);
  const canUseScreenPermissions = userPermissions.length > 0;
  const lastAllowedPath =
    typeof window !== "undefined"
      ? window.localStorage.getItem("posAdmin.lastAllowedPath")
      : "";

  const canAccessPath = (pathname) =>
    canUseScreenPermissions
      ? hasExactRoutePermission(user, pathname)
      : edgeMenuAccess.canAccessFrontendRoute(pathname);

  const resolveFallbackPath = () => {
    const candidatePaths = [lastAllowedPath, ...FALLBACK_PATHS];
    const fallback = candidatePaths.find(
      (pathname) =>
        pathname &&
        pathname !== location.pathname &&
        canAccessPath(pathname),
    );
    return fallback || "/not-authorized";
  };

  if (!isAuthenticated) {
    return children;
  }

  if (location.pathname === "/not-authorized") {
    return children;
  }

  if (!canUseScreenPermissions && (isLoading || isError || !Array.isArray(edgeMenu))) {
    return children;
  }

  if (!canAccessPath(location.pathname)) {
    return (
      <Navigate
        to={resolveFallbackPath()}
        replace
        state={{
          accessDenied: {
            message: ACCESS_DENIED_MESSAGE,
            sourcePath: location.pathname,
          },
        }}
      />
    );
  }

  return children;
};

export default EdgeMenuAccessGate;
